import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import DescriptionCell from '../components/DescriptionCell';
import './Checkout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const formatPrice = (p) =>
  p != null ? `₹${parseFloat(p).toFixed(2)}` : '—';

const formatDate = (date) =>
  date.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const toISODate = (date) => date.toISOString().split('T')[0];

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isAfterCutoffTime = (cutoffTimeStr) => {
  if (!cutoffTimeStr) return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return new Date() >= cutoff;
};

const nextWeekend = (baseDate) => {
  const d = new Date(baseDate);
  const dow = d.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const satDate = new Date(d);
  satDate.setDate(d.getDate() + daysToSat);
  const sunDate = new Date(d);
  sunDate.setDate(d.getDate() + daysToSun);
  return satDate <= sunDate
    ? { sat: satDate, sun: sunDate }
    : { sat: sunDate, sun: satDate };
};

const computeDeliveryDates = (product) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const extra = isAfterCutoffTime(product.order_cutoff_time) ? 1 : 0;
  const base = addDays(new Date(), lead + extra);
  if (product.days_available === 'WE') {
    const { sat, sun } = nextWeekend(base);
    return [sat, sun];
  }
  return [base];
};

const windowMatchesDate = (tw, date) => {
  const dow = date.getDay();
  const abbr = DAY_ABBR[dow];
  const code = tw.days_of_week;
  if (!code || code === 'AD') return true;
  if (code === 'WD') return dow >= 1 && dow <= 5;
  if (code === 'WE') return dow === 0 || dow === 6;
  return code.split(',').map((d) => d.trim()).includes(abbr);
};

const formatSlot = (tw) =>
  tw ? `${tw.start_time.slice(0, 5)} – ${tw.end_time.slice(0, 5)}` : '—';

// Intersection of time windows across ALL items for a date.
// Returns deduplicated {start_time, end_time} objects.
const computeCommonWindows = (windowsPerItem) => {
  const constraining = windowsPerItem.filter((ws) => ws.length > 0);
  if (constraining.length === 0) return [];
  let candidates = constraining[0].map((tw) => ({ start_time: tw.start_time, end_time: tw.end_time }));
  for (let i = 1; i < constraining.length; i++) {
    const intersected = [];
    for (const cand of candidates) {
      for (const tw of constraining[i]) {
        const s = cand.start_time > tw.start_time ? cand.start_time : tw.start_time;
        const e = cand.end_time   < tw.end_time   ? cand.end_time   : tw.end_time;
        if (s < e) intersected.push({ start_time: s, end_time: e });
      }
    }
    candidates = intersected;
  }
  const seen = new Set();
  return candidates.filter((c) => {
    const k = `${c.start_time}|${c.end_time}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// Per-item windows that overlap with at least one other item ("social" windows).
const computeSocialWindows = (itemWindows, otherItemsWindows) =>
  itemWindows.filter((myWin) =>
    otherItemsWindows.some((otherWins) =>
      otherWins.some((tw) => {
        const s = myWin.start_time > tw.start_time ? myWin.start_time : tw.start_time;
        const e = myWin.end_time   < tw.end_time   ? myWin.end_time   : tw.end_time;
        return s < e;
      })
    )
  );

// Assign windowMode, socialWindows, and selectedWindow to every enriched entry.
// Per date:
//   'common' — all items share a common intersection → one group, group-level window selector.
//   'social'  — no full common window → per-item social windows, individual selectors.
const assignWindowModes = (enrichedMap) => {
  // Group entries by date
  const byDate = {};
  Object.values(enrichedMap).forEach((e) => {
    if (!byDate[e.deliveryDateStr]) byDate[e.deliveryDateStr] = [];
    byDate[e.deliveryDateStr].push(e);
  });

  const result = {};
  Object.values(byDate).forEach((dateEntries) => {
    const common = computeCommonWindows(dateEntries.map((e) => e.windows));

    if (common.length > 0) {
      // All items share at least one common window — use group mode
      // Preserve a previously chosen common window when recomputing after a date change
      const prevSel = dateEntries.find((e) => e.selectedWindow)?.selectedWindow;
      const selected = (prevSel && common.find(
        (w) => w.start_time === prevSel.start_time && w.end_time === prevSel.end_time
      )) || common[0];
      dateEntries.forEach((e) => {
        result[e.product.id] = { ...e, windowMode: 'common', socialWindows: common, selectedWindow: selected };
      });
    } else {
      // No full common window — per-item social windows
      dateEntries.forEach((e) => {
        const otherWins = dateEntries.filter((o) => o.product.id !== e.product.id).map((o) => o.windows);
        const social = computeSocialWindows(e.windows, otherWins);
        const socialWindows = social.length > 0 ? social : [...e.windows];
        const prevSel = e.selectedWindow;
        const selected = (prevSel && socialWindows.some(
          (w) => w.start_time === prevSel.start_time && w.end_time === prevSel.end_time
        )) ? prevSel : (socialWindows[0] || null);
        result[e.product.id] = { ...e, windowMode: 'social', socialWindows, selectedWindow: selected };
      });
    }
  });
  return result;
};

// Group items by delivery date, then cluster by overlapping selected windows.
// Items whose windows mutually intersect land in one order; the slot shown is
// the intersection of all windows in that cluster (the narrowest common slot).
const buildOrderGroups = (enrichedMap) => {
  const byDate = {};
  Object.values(enrichedMap).forEach((entry) => {
    if (!byDate[entry.deliveryDateStr]) byDate[entry.deliveryDateStr] = [];
    byDate[entry.deliveryDateStr].push(entry);
  });

  const groups = [];
  Object.entries(byDate).forEach(([dateStr, dateEntries]) => {
    const noWindow = dateEntries.filter((e) => !e.selectedWindow);

    // Non-pack items with no window → solo groups
    noWindow.filter((e) => !e.product.isPack).forEach((e) => {
      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(e.deliveryDate),
        slot: '—',
        items: [{ product: e.product, quantity: e.quantity }],
        total: parseFloat(e.product.price) * e.quantity,
      });
    });

    // Pack combos with no window → one group per menu_pack_id (all combos together)
    const packsByMid = {};
    noWindow.filter((e) => e.product.isPack).forEach((e) => {
      const mid = e.product.menu_pack_id;
      if (!packsByMid[mid]) packsByMid[mid] = [];
      packsByMid[mid].push(e);
    });
    Object.values(packsByMid).forEach((entries) => {
      const rep = entries[0];
      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(rep.deliveryDate),
        slot: '—',
        items: entries.map(({ product, quantity }) => ({ product, quantity })),
        total: entries.reduce((s, e) => s + parseFloat(e.product.price) * e.quantity, 0),
      });
    });

    // Items with a window: sort by start_time then greedily cluster by overlap
    const windowed = dateEntries
      .filter((e) => e.selectedWindow)
      .sort((a, b) => a.selectedWindow.start_time.localeCompare(b.selectedWindow.start_time));

    const unassigned = [...windowed];
    while (unassigned.length > 0) {
      const cluster = [unassigned.shift()];
      let ix = { start_time: cluster[0].selectedWindow.start_time, end_time: cluster[0].selectedWindow.end_time };

      let i = 0;
      while (i < unassigned.length) {
        const entry  = unassigned[i];
        const s = ix.start_time > entry.selectedWindow.start_time ? ix.start_time : entry.selectedWindow.start_time;
        const e = ix.end_time   < entry.selectedWindow.end_time   ? ix.end_time   : entry.selectedWindow.end_time;
        if (s < e) {
          cluster.push(entry);
          ix = { start_time: s, end_time: e };
          unassigned.splice(i, 1);
        } else {
          i++;
        }
      }

      groups.push({
        deliveryDateStr: dateStr,
        deliveryDateLabel: formatDate(cluster[0].deliveryDate),
        slot: formatSlot(ix),
        items: cluster.map(({ product, quantity }) => ({ product, quantity })),
        total: cluster.reduce((sum, { product, quantity }) => sum + parseFloat(product.price) * quantity, 0),
      });
    }
  });

  return groups.sort((a, b) => a.deliveryDateStr.localeCompare(b.deliveryDateStr) || a.slot.localeCompare(b.slot));
};

const r2 = (n) => Math.round(n * 100) / 100;

// Build the per-item payload sent to the backend order API.
// Pack items carry menu_pack_id + selectionSummary instead of product_id.
const buildItemPayload = ({ product, quantity }) =>
  product.isPack
    ? {
        pack_id:          product.pack_id,
        qty:              quantity,
        unit_price:       parseFloat(product.price),
        total_price:      parseFloat(product.price) * quantity,
        selectionSummary: product.selectionSummary,
      }
    : {
        product_id:  product.id,
        qty:         quantity,
        unit_price:  parseFloat(product.price),
        total_price: parseFloat(product.price) * quantity,
      };

/* ─────────────────────────────────────────────────────── */

const Checkout = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // phase: loading | slot_select | summary | placing | confirmed
  const [phase, setPhase] = useState('loading');
  const [enriched, setEnriched] = useState(null);
  const [orderGroups, setOrderGroups] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [orderError, setOrderError] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);

  // Wallet state
  const [walletBalance, setWalletBalance] = useState(null);  // { cash_balance, points_balance }
  const [walletCashUsed, setWalletCashUsed] = useState(0);
  const [walletPointsUsed, setWalletPointsUsed] = useState(0);

  const isStorePartner = user?.user_type === 'S';
  const isLive = isStorePartner && (localStorage.getItem('checkoutMode') || 'live') === 'live';
  const isMobile = window.innerWidth < 768;
  const bypassPayment = user?.bypass_payment === 'Y';

  // QR payment state (store partners on mobile only)
  const [qrModal, setQrModal] = useState({ open: false, qrId: null, imageUrl: null, amount: 0 });
  const qrPollRef = useRef(null);

  const items = Object.values(cart);
  const authHeader = { Authorization: `Bearer ${user?.token}` };

  // Fetch delivery address
  useEffect(() => {
    if (!user?.customer_id) return;
    axios
      .get(`${API_URL}/menuRoutes/customer-address`, {
        params: { customer_id: user.customer_id },
        headers: authHeader,
      })
      .then((r) => setCustomerInfo(r.data))
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Compute delivery dates + load time windows
  useEffect(() => {
    if (items.length === 0) { setPhase('summary'); return; }

    if (isLive) {
      const today = new Date();
      const todayStr = toISODate(today);
      setOrderGroups([{
        deliveryDateStr: todayStr,
        deliveryDateLabel: formatDate(today),
        slot: '—',
        items: items.map(({ product, quantity }) => ({ product, quantity })),
        total: items.reduce((s, { product, quantity }) => s + parseFloat(product.price) * quantity, 0),
      }]);
      setPhase('summary');
      return;
    }

    const load = async () => {
      const nonPackItems    = items.filter(({ product }) => product.id !== 1047 && !product.isPack);
      const packItems       = items.filter(({ product }) => product.isPack);
      const deliveryFeeItem = items.find(({ product }) => product.id === 1047) || null;

      const deliveryMap = {};
      nonPackItems.forEach(({ product, quantity }) => {
        const possibleDates = computeDeliveryDates(product);
        const selectedDate = possibleDates[0];
        deliveryMap[product.id] = {
          product, quantity,
          possibleDates,
          selectedDate,
          deliveryDate: selectedDate,
          deliveryDateStr: toISODate(selectedDate),
          allWindows: [],
          windows: [],
          selectedWindow: null,
        };
      });

      // Pack combos: delivery metadata stored at add-time; windows fetched separately by menu_pack_id
      packItems.forEach(({ product, quantity }) => {
        const possibleDates = computeDeliveryDates(product);
        const selectedDate = possibleDates[0];
        deliveryMap[product.id] = {
          product, quantity,
          possibleDates,
          selectedDate,
          deliveryDate: selectedDate,
          deliveryDateStr: toISODate(selectedDate),
          allWindows: [], windows: [], selectedWindow: null,
        };
      });

      const productIds   = nonPackItems.map(({ product }) => product.id).filter(Boolean);
      // Collect distinct menu_pack_ids from all pack combos in cart
      const menuPackIds  = [...new Set(packItems.map(({ product }) => product.menu_pack_id).filter(Boolean))];

      try {
        const [productWindowsRes, packWindowsRes] = await Promise.all([
          productIds.length > 0
            ? axios.get(`${API_URL}/menuRoutes/product-windows`, {
                params: { product_ids: productIds.join(',') },
                headers: authHeader,
              })
            : Promise.resolve({ data: [] }),
          menuPackIds.length > 0
            ? axios.get(`${API_URL}/menuRoutes/pack-windows`, {
                params: { menu_pack_ids: menuPackIds.join(',') },
                headers: authHeader,
              })
            : Promise.resolve({ data: [] }),
        ]);

        // Populate regular product windows
        const byProductId = {};
        productWindowsRes.data.forEach((tw) => {
          if (!byProductId[tw.product_id]) byProductId[tw.product_id] = [];
          byProductId[tw.product_id].push(tw);
        });

        // Populate pack windows (all combos of the same menu_pack_id share the same windows)
        const byMenuPackId = {};
        packWindowsRes.data.forEach((tw) => {
          if (!byMenuPackId[tw.menu_pack_id]) byMenuPackId[tw.menu_pack_id] = [];
          byMenuPackId[tw.menu_pack_id].push(tw);
        });

        Object.values(deliveryMap).forEach((entry) => {
          if (entry.product.isPack) {
            const all = byMenuPackId[entry.product.menu_pack_id] || [];
            entry.allWindows = all;
            entry.windows    = all.filter((tw) => windowMatchesDate(tw, entry.selectedDate));
          } else {
            const all = byProductId[entry.product.id] || [];
            entry.allWindows = all;
            entry.windows    = all.filter((tw) => windowMatchesDate(tw, entry.selectedDate));
          }
        });
      } catch (e) {
        console.error('Failed to load time windows', e);
      }

      const enrichedMap = assignWindowModes(deliveryMap);
      setEnriched(enrichedMap);

      const needsSlotPick =
        Object.values(enrichedMap).some(({ possibleDates }) => possibleDates.length > 1) ||
        Object.values(enrichedMap).some(({ socialWindows }) => socialWindows.length > 1);

      if (needsSlotPick) {
        setPhase('slot_select');
      } else {
        let groups = buildOrderGroups(enrichedMap);
        if (deliveryFeeItem && groups.length > 0) {
          groups[0] = {
            ...groups[0],
            items: [...groups[0].items, { product: deliveryFeeItem.product, quantity: deliveryFeeItem.quantity }],
            total: groups[0].total + parseFloat(deliveryFeeItem.product.price) * deliveryFeeItem.quantity,
          };
        }
        setOrderGroups(groups);
        setPhase('summary');
      }
    };

    load();
  }, []); // eslint-disable-line

  // Fetch wallet balance when summary phase begins; auto-fill if funds available
  useEffect(() => {
    if (phase !== 'summary' || !user?.customer_id) return;
    axios
      .get(`${API_URL}/wallet/balance`, { headers: authHeader })
      .then((res) => {
        setWalletBalance(res.data);
        const cash   = parseFloat(res.data.cash_balance)   || 0;
        const points = parseFloat(res.data.points_balance) || 0;
        if (cash > 0 || points > 0) {
          const total     = orderGroups.reduce((s, g) => s + g.total, 0);
          const autoCash   = r2(Math.min(cash, total));
          const autoPoints = r2(Math.min(points, Math.max(0, total - autoCash)));
          setWalletCashUsed(autoCash);
          setWalletPointsUsed(autoPoints);
        }
      })
      .catch(() => {});
  }, [phase]); // eslint-disable-line

  const handleSlotChange = (productId, idx) => {
    setEnriched((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], selectedWindow: prev[productId].socialWindows[parseInt(idx, 10)] },
    }));
  };

  // For common-mode dates: update selectedWindow for ALL items on that date
  const handleGroupWindowChange = (dateStr, idx) => {
    setEnriched((prev) => {
      const updated = { ...prev };
      Object.values(updated).forEach((entry) => {
        if (entry.deliveryDateStr === dateStr && entry.windowMode === 'common') {
          updated[entry.product.id] = {
            ...entry,
            selectedWindow: entry.socialWindows[parseInt(idx, 10)],
          };
        }
      });
      return updated;
    });
  };

  // For common-mode dates: move ALL items in the group to a new delivery date
  const handleGroupDateChange = (dateStr, dateIso) => {
    const updated = { ...enriched };
    Object.values(updated).forEach((entry) => {
      if (entry.deliveryDateStr !== dateStr || entry.windowMode !== 'common') return;
      const newDate = entry.possibleDates.find((d) => toISODate(d) === dateIso);
      if (!newDate) return;
      updated[entry.product.id] = {
        ...entry,
        selectedDate: newDate,
        deliveryDate: newDate,
        deliveryDateStr: dateIso,
        windows: entry.allWindows.filter((tw) => windowMatchesDate(tw, newDate)),
      };
    });
    setEnriched(assignWindowModes(updated));
  };

  const handleDateChange = (productId, dateIso) => {
    const entry = enriched[productId];
    const newDate = entry.possibleDates.find((d) => toISODate(d) === dateIso);
    const filtered = entry.allWindows.filter((tw) => windowMatchesDate(tw, newDate));
    const updated = {
      ...enriched,
      [productId]: { ...entry, selectedDate: newDate, deliveryDate: newDate, deliveryDateStr: dateIso, windows: filtered },
    };
    setEnriched(assignWindowModes(updated));
  };

  const handleConfirmSlots = () => {
    const deliveryFeeItem = items.find(({ product }) => product.id === 1047) || null;
    const groups = buildOrderGroups(enriched);
    if (deliveryFeeItem && groups.length > 0) {
      groups[0] = {
        ...groups[0],
        items: [...groups[0].items, { product: deliveryFeeItem.product, quantity: deliveryFeeItem.quantity }],
        total: groups[0].total + parseFloat(deliveryFeeItem.product.price) * deliveryFeeItem.quantity,
      };
    }
    setOrderGroups(groups);
    setPhase('summary');
  };

  const cancelPending = (orderId) => {
    if (!orderId) return;
    axios.post(`${API_URL}/pay/cancel-pending`, { razorpay_order_id: orderId }, { headers: authHeader })
      .catch(() => {});
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const closeQrModal = () => {
    clearInterval(qrPollRef.current);
    const { qrId } = qrModal;
    setQrModal({ open: false, qrId: null, imageUrl: null, amount: 0 });
    if (qrId) {
      axios.post(`${API_URL}/pay/cancel-qr`, { qr_id: qrId }, { headers: authHeader }).catch(() => {});
    }
  };

  const handleQrCheckout = async () => {
    setOrderError(null);
    setPhase('placing');
    try {
      const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
      const { data } = await axios.post(
        `${API_URL}/pay/create-qr`,
        {
          amount:         totalAmount,
          orderGroups:    orderGroups.map((g) => ({
            deliveryDateStr:   g.deliveryDateStr,
            deliveryDateLabel: g.deliveryDateLabel,
            slot:              g.slot,
            total:             g.total,
            items: g.items.map(buildItemPayload),
          })),
          customerId:     user?.customer_id || null,
          customerName:   customerInfo?.customer_name || null,
          salesChannelId: isLive ? 3 : null,
          mode:           isLive ? 'live' : 'prebook',
        },
        { headers: authHeader }
      );

      setPhase('summary');
      setQrModal({ open: true, qrId: data.qr_id, imageUrl: data.image_url, amount: data.amount });

      qrPollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API_URL}/pay/qr-status/${data.qr_id}`,
            { headers: authHeader }
          );
          if (statusRes.data.paid) {
            clearInterval(qrPollRef.current);
            clearCart();
            setConfirmations(statusRes.data.confirmations || []);
            setQrModal({ open: false, qrId: null, imageUrl: null, amount: 0 });
            setPhase('confirmed');
          }
        } catch (_) {}
      }, 3000);
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to generate QR code. Please try again.');
      setPhase('summary');
    }
  };

  const handlePlaceOrders = async () => {
    setPhase('placing');
    setOrderError(null);

    const totalAmount  = orderGroups.reduce((s, g) => s + g.total, 0);
    const usedCash     = r2(Math.min(walletCashUsed   || 0, totalAmount));
    const usedPoints   = r2(Math.min(walletPointsUsed || 0, Math.max(0, totalAmount - usedCash)));
    const razorpayDue  = r2(Math.max(0, totalAmount - usedCash - usedPoints));

    const orderPayload = {
      amount:           totalAmount,
      walletCashAmount:   usedCash,
      walletPointsAmount: usedPoints,
      orderGroups: orderGroups.map((g) => ({
        deliveryDateStr:   g.deliveryDateStr,
        deliveryDateLabel: g.deliveryDateLabel,
        slot:              g.slot,
        total:             g.total,
        items: g.items.map(buildItemPayload),
      })),
      customerId:     user?.customer_id || null,
      customerName:   customerInfo?.customer_name || null,
      salesChannelId: isLive ? 3 : null,
      mode:           isLive ? 'live' : 'prebook',
    };

    // ── Pure wallet: no Razorpay modal needed ──────────────────
    if (razorpayDue < 0.01 && (usedCash > 0 || usedPoints > 0)) {
      try {
        const { data } = await axios.post(`${API_URL}/pay/create-order`, orderPayload, { headers: authHeader });
        if (data.wallet_only) {
          clearCart();
          setConfirmations(data.confirmations);
          setPhase('confirmed');
          return;
        }
      } catch (err) {
        setOrderError(err.response?.data?.error || 'Failed to place order. Please try again.');
        setPhase('summary');
        return;
      }
    }

    // ── Razorpay (full or split) ───────────────────────────────
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setOrderError('Failed to load payment SDK. Please check your connection and try again.');
      setPhase('summary');
      return;
    }

    try {
      setPendingOrderId(null);
      const { data } = await axios.post(`${API_URL}/pay/create-order`, orderPayload, { headers: authHeader });

      setPendingOrderId(data.razorpay_order_id);

      const options = {
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:        'Local Heros Foods Pvt Ltd',
        description: `${orderGroups.length} order${orderGroups.length > 1 ? 's' : ''} · ${orderGroups.reduce((s, g) => s + g.items.reduce((a, i) => a + i.quantity, 0), 0)} items`,
        prefill: {
          name:    customerInfo?.customer_name || '',
          contact: user?.phone || '',
        },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            const res = await axios.post(
              `${API_URL}/pay/verify-and-place`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              },
              { headers: authHeader }
            );
            clearCart();
            setConfirmations(res.data.confirmations);
            setPhase('confirmed');
          } catch (err) {
            setOrderError(err.response?.data?.error || 'Payment received but order placement failed. Please contact support.');
            setPhase('summary');
          }
        },
        modal: {
          ondismiss: () => {
            cancelPending(data.razorpay_order_id);
            setPhase('summary');
          },
        },
        config: {
          display: {
            blocks: {
              upiQr: {
                name: 'Pay via UPI QR Code',
                instruments: [{ method: 'upi', flows: ['qr'] }],
              },
            },
            sequence: ['block.upiQr'],
            preferences: { show_default_blocks: true },
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setOrderError(`Payment failed: ${response.error.description}`);
        setPhase('summary');
      });
      rzp.open();
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPhase('summary');
    }
  };

  const handleBypassSubmit = async () => {
    setPhase('placing');
    setOrderError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/pay/bypass-place-order`,
        {
          orderGroups: orderGroups.map((g) => ({
            deliveryDateStr:   g.deliveryDateStr,
            deliveryDateLabel: g.deliveryDateLabel,
            slot:              g.slot,
            total:             g.total,
            items: g.items.map(buildItemPayload),
          })),
          customerId:     user?.customer_id || null,
          customerName:   customerInfo?.customer_name || null,
          salesChannelId: isLive ? 3 : null,
          mode:           isLive ? 'live' : 'prebook',
        },
        { headers: authHeader }
      );
      clearCart();
      setConfirmations(data.confirmations);
      setPhase('confirmed');
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Failed to place order. Please try again.');
      setPhase('summary');
    }
  };

  // Clamp wallet inputs to valid range whenever they change
  const handleCashInput = (val) => {
    const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
    const maxCash = parseFloat(walletBalance?.cash_balance || 0);
    const parsed = parseFloat(val) || 0;
    setWalletCashUsed(r2(Math.max(0, Math.min(parsed, maxCash, totalAmount))));
  };

  const handlePointsInput = (val) => {
    const totalAmount = orderGroups.reduce((s, g) => s + g.total, 0);
    const maxPoints = parseFloat(walletBalance?.points_balance || 0);
    const parsed = parseFloat(val) || 0;
    const remaining = Math.max(0, totalAmount - (walletCashUsed || 0));
    setWalletPointsUsed(r2(Math.max(0, Math.min(parsed, maxPoints, remaining))));
  };

  /* ── Empty cart ── */
  if (items.length === 0 && phase !== 'confirmed') {
    return (
      <div className="checkout-empty">
        <p>Your cart is empty.</p>
        <button className="checkout-back-btn" onClick={() => navigate('/home')}>Browse Products</button>
      </div>
    );
  }

  /* ── Loading ── */
  if (phase === 'loading') {
    return (
      <div className="checkout-empty">
        <p style={{ color: '#999' }}>Calculating delivery dates…</p>
      </div>
    );
  }

  /* ── Confirmed ── */
  if (phase === 'confirmed') {
    return (
      <div className="checkout-confirmation">
        <div className="checkout-confirmation-icon">&#10003;</div>
        <h2 className="checkout-confirmation-title">
          {confirmations.length === 1 ? 'Order Placed!' : `${confirmations.length} Orders Placed!`}
        </h2>
        {confirmations.map((c, i) => (
          <p key={i} className="checkout-confirmation-msg">
            {isLive
              ? <>Order <strong>{c.order_no}</strong> has been confirmed.</>
              : <>Order <strong>{c.order_no}</strong> — delivery on <strong>{c.delivery_date}</strong>
                 {c.slot !== '—' && <>, slot <strong>{c.slot}</strong></>}.</>
            }
          </p>
        ))}
        <p className="checkout-confirmation-cod">{bypassPayment ? 'Your order has been placed.' : 'Payment successful! Your order is confirmed.'}</p>
        <button className="checkout-back-btn" onClick={() => navigate('/home')}>Continue Shopping</button>
      </div>
    );
  }

  /* ── Slot selection ── */
  if (phase === 'slot_select' && enriched) {
    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <button className="checkout-back-link" onClick={() => navigate('/cart')}>&#8592; Back to Cart</button>
          <h2 className="checkout-title">Select Delivery Date &amp; Slot</h2>
        </div>
        <div className="slot-select-body">
          {(() => {
            const regularEntries = Object.values(enriched).filter((e) => e.product.id !== 1047);

            // Derive one card per date for common-mode groups
            const commonGroups = {};
            regularEntries.filter((e) => e.windowMode === 'common').forEach((e) => {
              if (!commonGroups[e.deliveryDateStr]) {
                commonGroups[e.deliveryDateStr] = {
                  dateStr: e.deliveryDateStr, date: e.deliveryDate,
                  socialWindows: e.socialWindows, selectedWindow: e.selectedWindow,
                  entries: [],
                };
              }
              commonGroups[e.deliveryDateStr].entries.push(e);
            });

            // Social-mode items that still need user input
            const socialItems = regularEntries.filter(
              (e) => e.windowMode === 'social' && (e.possibleDates.length > 1 || e.socialWindows.length > 1)
            );

            return (
              <>
                {/* Common-mode: one card per date showing all items, group-level date + window selector */}
                {Object.values(commonGroups).map((grp) => {
                  const hasDateChoice = grp.entries.some((e) => e.possibleDates.length > 1);
                  return (
                    <div key={grp.dateStr} className="slot-item-card">
                      <div className="slot-item-info">
                        <span className="slot-item-name">&#128337; {formatDate(grp.date)}</span>
                        {grp.socialWindows.length === 1 && (
                          <span className="slot-window-pill">&#128336; {formatSlot(grp.selectedWindow)}</span>
                        )}
                      </div>
                      <p className="slot-group-items">
                        {grp.entries.map((e) => `${e.product.name} ×${e.quantity}`).join(' · ')}
                      </p>
                      <div className="slot-sections">
                        {hasDateChoice && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Date</span>
                            <div className="slot-date-btns">
                              {grp.entries[0].possibleDates.map((d) => {
                                const iso = toISODate(d);
                                return (
                                  <button
                                    key={iso}
                                    className={`slot-date-btn${grp.dateStr === iso ? ' slot-date-btn--active' : ''}`}
                                    onClick={() => handleGroupDateChange(grp.dateStr, iso)}
                                  >
                                    {formatDate(d)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {grp.socialWindows.length > 1 && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Window</span>
                            <div className="slot-date-btns">
                              {grp.socialWindows.map((w, i) => (
                                <button
                                  key={i}
                                  className={`slot-date-btn${
                                    grp.selectedWindow?.start_time === w.start_time &&
                                    grp.selectedWindow?.end_time   === w.end_time
                                      ? ' slot-date-btn--active' : ''
                                  }`}
                                  onClick={() => handleGroupWindowChange(grp.dateStr, i)}
                                >
                                  {formatSlot(w)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Social-mode: per-item cards for date choice and/or individual window choice */}
                {socialItems.map((entry) => {
                  const needsDateChoice   = entry.possibleDates.length > 1;
                  const needsWindowChoice = entry.socialWindows.length > 1;
                  return (
                    <div key={entry.product.id} className="slot-item-card">
                      <div className="slot-item-info">
                        {entry.product.image_url
                          ? <img className="slot-item-img" src={entry.product.image_url} alt={entry.product.name} />
                          : <span className="slot-item-placeholder">&#128230;</span>}
                        <span className="slot-item-name">{entry.product.name}</span>
                        <span className="slot-item-qty">&#215; {entry.quantity}</span>
                        {!needsWindowChoice && entry.selectedWindow && (
                          <span className="slot-window-pill">&#128336; {formatSlot(entry.selectedWindow)}</span>
                        )}
                      </div>
                      <div className="slot-sections">
                        {needsDateChoice && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Date</span>
                            <div className="slot-date-btns">
                              {entry.possibleDates.map((d) => {
                                const iso = toISODate(d);
                                return (
                                  <button
                                    key={iso}
                                    className={`slot-date-btn${entry.deliveryDateStr === iso ? ' slot-date-btn--active' : ''}`}
                                    onClick={() => handleDateChange(entry.product.id, iso)}
                                  >
                                    {formatDate(d)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {needsWindowChoice && (
                          <div className="slot-section">
                            <span className="slot-section-label">Delivery Window</span>
                            <div className="slot-date-btns">
                              {entry.socialWindows.map((w, i) => (
                                <button
                                  key={i}
                                  className={`slot-date-btn${
                                    entry.selectedWindow?.start_time === w.start_time &&
                                    entry.selectedWindow?.end_time   === w.end_time
                                      ? ' slot-date-btn--active' : ''
                                  }`}
                                  onClick={() => handleSlotChange(entry.product.id, i)}
                                >
                                  {formatSlot(w)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          <div className="slot-actions">
            <button className="checkout-place-btn" style={{ maxWidth: 320 }} onClick={handleConfirmSlots}>
              Continue to Summary &#8594;
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Summary / Placing ── */
  const totalAll   = orderGroups.reduce((s, g) => s + g.total, 0);
  const totalUnits = orderGroups.reduce((s, g) => s + g.items.reduce((a, i) => a + i.quantity, 0), 0);

  const usedCash    = r2(Math.min(walletCashUsed   || 0, totalAll));
  const usedPoints  = r2(Math.min(walletPointsUsed || 0, Math.max(0, totalAll - usedCash)));
  const razorpayDue = r2(Math.max(0, totalAll - usedCash - usedPoints));

  const hasWallet  = walletBalance && (
    parseFloat(walletBalance.cash_balance) > 0 || parseFloat(walletBalance.points_balance) > 0
  );

  const btnLabel = phase === 'placing'
    ? 'Processing…'
    : razorpayDue < 0.01 && (usedCash > 0 || usedPoints > 0)
      ? 'Pay with Wallet'
      : (usedCash > 0 || usedPoints > 0)
        ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
        : orderGroups.length > 1
          ? `Pay for ${orderGroups.length} Orders`
          : 'Pay Now';

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <button className="checkout-back-link" onClick={() => navigate('/cart')}>&#8592; Back to Cart</button>
        <h2 className="checkout-title">Order Summary</h2>
        {orderGroups.length > 1 && (
          <p className="checkout-multi-note">
            Your cart will be split into {orderGroups.length} orders by delivery date / slot.
          </p>
        )}
      </div>

      <div className="checkout-summary-layout">
        {/* Left: order group cards */}
        <div className="checkout-groups">
          {orderGroups.map((group, gi) => (
            <div key={gi} className="checkout-group-card">
              <div className="checkout-group-header">
                {orderGroups.length > 1 && (
                  <span className="checkout-group-badge">Order {gi + 1}</span>
                )}
                {!isLive && <span className="checkout-group-date">&#128337; {group.deliveryDateLabel}</span>}
                {!isLive && group.slot !== '—' && (
                  <span className="checkout-group-slot">&#128336; {group.slot}</span>
                )}
              </div>
              <div className="checkout-items">
                {group.items.map(({ product, quantity }) => {
                  const lineTotal = product.price != null ? parseFloat(product.price) * quantity : null;
                  return (
                    <div key={product.id} className="checkout-item-row">
                      <div className="checkout-item-image">
                        {product.image_url
                          ? <img src={product.image_url} alt={product.name} />
                          : <span className="checkout-item-placeholder">&#128230;</span>}
                      </div>
                      <div className="checkout-item-info">
                        <p className="checkout-item-name">{product.name}</p>
                        {product.description && <DescriptionCell text={product.description} className="checkout-item-desc" />}
                        <p className="checkout-item-unit">{formatPrice(product.price)} each</p>
                      </div>
                      <div className="checkout-item-qty">
                        <span className="checkout-qty-label">Qty</span>
                        <span className="checkout-qty-value">{quantity}</span>
                      </div>
                      <div className="checkout-item-total">
                        {lineTotal != null ? formatPrice(lineTotal) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
              {orderGroups.length > 1 && (
                <div className="checkout-group-subtotal">
                  <span>Subtotal</span>
                  <span>{formatPrice(group.total)}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right: address + wallet + total */}
        <div className="checkout-summary-panel">
          <div className="checkout-card">
            <h3 className="checkout-card-title">&#128205; Delivery Address</h3>
            {!user?.customer_id ? (
              <p className="checkout-address-missing">No customer account linked.</p>
            ) : !customerInfo ? (
              <p className="checkout-address-loading">Loading address…</p>
            ) : (
              <div className="checkout-address-block">
                <p className="checkout-address-name">{customerInfo.customer_name}</p>
                <p className="checkout-address-text">
                  {customerInfo.address || <span className="checkout-address-missing">No address on file.</span>}
                </p>
              </div>
            )}
          </div>

          {/* Wallet card — hidden for bypass-payment users */}
          {hasWallet && !bypassPayment && (
            <div className="checkout-card checkout-wallet-card">
              <h3 className="checkout-card-title">&#128179; Pay from Wallet</h3>

              {parseFloat(walletBalance.cash_balance) > 0 && (
                <div className="checkout-wallet-row">
                  <div className="checkout-wallet-avail">
                    <span className="checkout-wallet-label">Cash</span>
                    <span className="checkout-wallet-bal">
                      Available: {formatPrice(walletBalance.cash_balance)}
                    </span>
                  </div>
                  <input
                    type="number"
                    className="checkout-wallet-input"
                    min="0"
                    max={parseFloat(walletBalance.cash_balance)}
                    step="0.01"
                    value={walletCashUsed}
                    onChange={(e) => handleCashInput(e.target.value)}
                  />
                </div>
              )}

              {parseFloat(walletBalance.points_balance) > 0 && (
                <div className="checkout-wallet-row">
                  <div className="checkout-wallet-avail">
                    <span className="checkout-wallet-label">Reward Points</span>
                    <span className="checkout-wallet-bal">
                      Available: {parseFloat(walletBalance.points_balance).toFixed(0)} pts
                    </span>
                  </div>
                  <input
                    type="number"
                    className="checkout-wallet-input"
                    min="0"
                    max={parseFloat(walletBalance.points_balance)}
                    step="1"
                    value={walletPointsUsed}
                    onChange={(e) => handlePointsInput(e.target.value)}
                  />
                </div>
              )}

              {(usedCash > 0 || usedPoints > 0) && (
                <div className="checkout-wallet-deduct-row">
                  <span>Wallet deduction</span>
                  <span>- {formatPrice(usedCash + usedPoints)}</span>
                </div>
              )}

              {razorpayDue > 0.01 && (usedCash > 0 || usedPoints > 0) && (
                <div className="checkout-razorpay-due-row">
                  <span>Pay via Razorpay</span>
                  <span className="checkout-razorpay-due-amt">{formatPrice(razorpayDue)}</span>
                </div>
              )}
            </div>
          )}

          <div className="checkout-card checkout-total-card">
            <div className="checkout-total-row">
              <span>
                Total ({totalUnits} unit{totalUnits !== 1 ? 's' : ''}
                {orderGroups.length > 1 ? `, ${orderGroups.length} orders` : ''})
              </span>
              <span className="checkout-total-amount">{formatPrice(totalAll)}</span>
            </div>
            {orderError && <p className="checkout-order-error">{orderError}</p>}
            {bypassPayment ? (
              <button
                className="checkout-place-btn"
                onClick={handleBypassSubmit}
                disabled={phase === 'placing'}
              >
                {phase === 'placing' ? 'Placing Order…' : 'Submit Order'}
              </button>
            ) : (
              <>
                <button
                  className="checkout-place-btn"
                  onClick={handlePlaceOrders}
                  disabled={phase === 'placing'}
                >
                  {btnLabel}
                </button>
                {isStorePartner && isMobile && (
                  <button
                    className="checkout-place-btn checkout-qr-btn"
                    onClick={handleQrCheckout}
                    disabled={phase === 'placing'}
                  >
                    Pay via UPI QR — {formatPrice(totalAll)}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {qrModal.open && (
        <div className="checkout-qr-overlay">
          <div className="checkout-qr-modal">
            <h3 className="checkout-qr-title">Scan to Pay {formatPrice(qrModal.amount)}</h3>
            <img src={qrModal.imageUrl} alt="UPI QR Code" className="checkout-qr-img" />
            <p className="checkout-qr-waiting">Waiting for payment…</p>
            <button className="checkout-qr-cancel" onClick={closeQrModal}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
